/**
 * Help & feedback as the app's entry points open it (SA-1310), kept out of `HelpFeedbackHost.tsx` so
 * that file exports components only (`react-refresh/only-export-components`).
 */
import { createContext, useContext, type MutableRefObject } from 'react'
import type { Opener } from './conversation'
import type { AnyTour } from '@/services/tour/tourModel'

export interface HelpFeedback {
  /** Strict: the loaded manifest holds `product_feedback`. */
  available: boolean
  /** Open the panel on Ask; a failure opener starts from what went wrong. */
  open: (opener?: Opener) => void
}

export const HelpFeedbackContext = createContext<HelpFeedback>({ available: false, open: () => {} })

/** The panel as entry points see it; outside a `HelpFeedbackHost` it is never available. */
export function useHelpFeedback(): HelpFeedback {
  return useContext(HelpFeedbackContext)
}

/** What the panel's parts need from the host. */
export interface PanelControls {
  close: () => void
  /** Begin again: a new conversation from the latest opener and a fresh snapshot. */
  startFresh: () => void
  /**
   * The Esc handler the composer sets while the mic listens: it stops listening and answers true, so
   * Esc does not also close the panel.
   */
  escape: MutableRefObject<(() => boolean) | null>
  /**
   * Play a tour, the panel folded to the tour's chip until it ends (SA-1315). `returnTo` names the
   * answer whose Yes takes the focus when the panel comes back (SA-1316).
   */
  startTour: (tour: AnyTour, returnTo?: number) => void
}

export const PanelControlsContext = createContext<PanelControls>({
  close: () => {},
  startFresh: () => {},
  escape: { current: null },
  startTour: () => {},
})

export function usePanelControls(): PanelControls {
  return useContext(PanelControlsContext)
}
